import React, { useEffect, useMemo, useState } from "react";
import useLanguage from "../hooks/useLanguage";
import {
  Alert,
  Button,
  Box,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import TopNavBar from "../components/TopNavBar";
import performanceTranslations from "../assets/variables/performanceTranslations";
import {
  getCleaningPerformanceFilterOptions,
  getCleaningPerformanceRows,
} from "../api/performance";
import GraphCard from "../components/performanceDashboard/GraphCard";
import PerformanceFiltersCard from "../components/performanceDashboard/PerformanceFiltersCard";
import OverviewCard from "../components/performanceDashboard/OverviewCard";
import TopPerformersCard from "../components/performanceDashboard/TopPerformersCard";

function formatNumber(value, locale = "da-DK") {
  return new Intl.NumberFormat(locale).format(value);
}

function formatPercent(value, locale = "da-DK") {
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)}%`;
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getLatestSelectableDateValue() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toDateInputValue(yesterday);
}

function countWeekdaysInRange(fromDateStr, toDateStr) {
  if (!fromDateStr || !toDateStr) return 0;
  const start = new Date(fromDateStr + "T00:00:00");
  const end = new Date(toDateStr + "T00:00:00");
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;

  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      count += 1;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

function subtractMonths(date, months) {
  const copy = new Date(date);
  copy.setMonth(copy.getMonth() - months);
  return copy;
}

function addDays(dateStr, days) {
  const date = new Date(dateStr + "T00:00:00");
  if (isNaN(date.getTime())) return dateStr;
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
}

// Must match the backend's REPORTING_WINDOW_START_HOUR_LOCAL / REPORTING_WINDOW_START_MINUTE_LOCAL.
const REPORTING_WINDOW_START_HOUR = 6;
const REPORTING_WINDOW_START_MINUTE = 25;

function parseBackendTimestamp(timestamp) {
  if (!timestamp) return null;

  if (timestamp instanceof Date) {
    return Number.isNaN(timestamp.getTime())
      ? null
      : new Date(timestamp.getTime());
  }

  if (typeof timestamp === "number") {
    const fromEpoch = new Date(timestamp);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const raw = String(timestamp).trim();
  if (!raw) return null;

  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const hasTimezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const utcCandidate = hasTimezoneSuffix ? normalized : `${normalized}Z`;

  const parsedUtc = new Date(utcCandidate);
  if (!Number.isNaN(parsedUtc.getTime())) {
    return parsedUtc;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function toWindowStartTimestamp(dateStr) {
  // The DB stores cleaning_start_time in UTC, but the reporting window
  // is a local wall-clock boundary, so convert local window-start -> UTC
  // before sending it to the API, instead of sending a literal time string.
  const localWindowStart = new Date(`${dateStr}T00:00:00`);
  localWindowStart.setHours(
    REPORTING_WINDOW_START_HOUR,
    REPORTING_WINDOW_START_MINUTE,
    0,
    0,
  );

  const yyyy = localWindowStart.getUTCFullYear();
  const mm = String(localWindowStart.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(localWindowStart.getUTCDate()).padStart(2, "0");
  const hh = String(localWindowStart.getUTCHours()).padStart(2, "0");
  const mi = String(localWindowStart.getUTCMinutes()).padStart(2, "0");
  const ss = String(localWindowStart.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function resolveApiDateRange(fromDate, toDate) {
  if (!fromDate || !toDate) {
    return { startDate: fromDate, endDate: toDate };
  }

  return {
    startDate: toWindowStartTimestamp(fromDate),
    endDate: toWindowStartTimestamp(addDays(toDate, 1)),
  };
}

function resolvePresetRange(preset, monthSpan) {
  const endDate = getLatestSelectableDateValue();
  const endDateObject = new Date(endDate + "T00:00:00");

  switch (preset) {
    case "week": {
      const start = new Date(endDateObject);
      start.setDate(start.getDate() - 6);
      return { startDate: toDateInputValue(start), endDate };
    }
    case "month": {
      return {
        startDate: toDateInputValue(subtractMonths(endDateObject, 1)),
        endDate,
      };
    }
    case "multiMonth": {
      return {
        startDate: toDateInputValue(subtractMonths(endDateObject, monthSpan)),
        endDate,
      };
    }
    case "year": {
      return {
        startDate: toDateInputValue(subtractMonths(endDateObject, 12)),
        endDate,
      };
    }
    default:
      return { startDate: endDate, endDate };
  }
}

function buildChartBuckets(fromDateStr, toDateStr, rows, locale) {
  const empty = {
    areaValues: [],
    taskValues: [],
    xLabels: [],
    xTooltipLabels: [],
    bucketKeys: [],
    isWeekendValues: [],
    operationalDayCount: 0,
    granularity: "month",
  };
  if (!fromDateStr || !toDateStr) return empty;
  const start = new Date(fromDateStr + "T00:00:00");
  const end = new Date(toDateStr + "T23:59:59");
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return empty;

  const daySpan = Math.max(1, Math.round((end - start) / 86400000));
  const granularity = daySpan <= 14 ? "day" : daySpan <= 90 ? "week" : "month";

  const bucketKeys = [];
  const bucketMap = new Map();
  const bestAreaByDayRoom = new Map();
  let operationalDayCount = 0;

  const resolveOperationalDate = (timestamp) => {
    const date = parseBackendTimestamp(timestamp);
    if (!date) return null;

    const operationalDate = new Date(date);
    if (
      date.getHours() < REPORTING_WINDOW_START_HOUR ||
      (date.getHours() === REPORTING_WINDOW_START_HOUR &&
        date.getMinutes() < REPORTING_WINDOW_START_MINUTE)
    ) {
      operationalDate.setDate(operationalDate.getDate() - 1);
    }
    operationalDate.setHours(0, 0, 0, 0);
    return operationalDate;
  };

  if (granularity === "day") {
    const d = new Date(start);
    while (d <= end) {
      const key = toDateInputValue(d);
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      if (!isWeekend) {
        operationalDayCount += 1;
      }
      bucketKeys.push(key);
      bucketMap.set(key, {
        area: 0,
        tasks: 0,
        isWeekend,
        label: d.toLocaleDateString(locale, { weekday: "short" }),
        tooltipLabel: d.toLocaleDateString(locale, {
          weekday: "short",
          day: "numeric",
          month: "short",
        }),
      });
      d.setDate(d.getDate() + 1);
    }
  } else if (granularity === "week") {
    const d = new Date(start);
    while (d <= end) {
      const key = toDateInputValue(d);
      bucketKeys.push(key);
      bucketMap.set(key, {
        area: 0,
        tasks: 0,
        isWeekend: false,
        label: d.toLocaleDateString(locale, { day: "numeric", month: "short" }),
        tooltipLabel: d.toLocaleDateString(locale, {
          day: "numeric",
          month: "short",
        }),
      });
      d.setDate(d.getDate() + 7);
    }
  } else {
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonthStart = new Date(end.getFullYear(), end.getMonth(), 1);
    while (d <= endMonthStart) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      bucketKeys.push(key);
      bucketMap.set(key, {
        area: 0,
        tasks: 0,
        isWeekend: false,
        label: d.toLocaleDateString(locale, { month: "short" }),
        tooltipLabel: d.toLocaleDateString(locale, { month: "short" }),
      });
      d.setMonth(d.getMonth() + 1);
    }
  }

  rows.forEach((row) => {
    const operationalDate = resolveOperationalDate(row?.cleaning_start_time);
    if (!operationalDate) return;
    if (operationalDate < start || operationalDate > end) return;
    // Weekends are excluded from every average/baseline calculation, so keep
    // them out of the bar totals too instead of only greying them out.
    const operationalWeekday = operationalDate.getDay();
    if (operationalWeekday === 0 || operationalWeekday === 6) return;

    const dayKey = toDateInputValue(operationalDate);

    let key;
    if (granularity === "day") {
      key = dayKey;
    } else if (granularity === "week") {
      for (let i = bucketKeys.length - 1; i >= 0; i--) {
        if (operationalDate >= new Date(bucketKeys[i] + "T00:00:00")) {
          key = bucketKeys[i];
          break;
        }
      }
    } else {
      key = `${operationalDate.getFullYear()}-${String(operationalDate.getMonth() + 1).padStart(2, "0")}`;
    }

    if (key && bucketMap.has(key)) {
      const areaCleaned = Number(row?.area_cleaned || 0);
      const roomPossibleArea = Number(
        row?.room_area_minus_inventory_minus_things_on_floor || 0,
      );
      const cappedAreaCleaned =
        Number.isFinite(roomPossibleArea) && roomPossibleArea > 0
          ? Math.min(areaCleaned, roomPossibleArea)
          : areaCleaned;
      const roomKey =
        row?.room_pk_id ??
        `${row?.building_name || ""}|${row?.department_name || ""}|${row?.room_name || ""}`;
      const dayRoomKey = `${dayKey}::${roomKey}`;
      const previousBest = bestAreaByDayRoom.get(dayRoomKey) || 0;
      if (cappedAreaCleaned > previousBest) {
        bestAreaByDayRoom.set(dayRoomKey, cappedAreaCleaned);
      }
    }
  });

  bestAreaByDayRoom.forEach((bestArea, dayRoomKey) => {
    const separatorIndex = dayRoomKey.indexOf("::");
    if (separatorIndex < 0) return;
    const dayKey = dayRoomKey.slice(0, separatorIndex);
    const dayDate = new Date(dayKey + "T00:00:00");
    if (isNaN(dayDate.getTime())) return;

    let bucketKey;
    if (granularity === "day") {
      bucketKey = dayKey;
    } else if (granularity === "week") {
      for (let i = bucketKeys.length - 1; i >= 0; i--) {
        if (dayDate >= new Date(bucketKeys[i] + "T00:00:00")) {
          bucketKey = bucketKeys[i];
          break;
        }
      }
    } else {
      bucketKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, "0")}`;
    }

    if (!bucketMap.has(bucketKey)) return;
    bucketMap.get(bucketKey).area += bestArea;
    bucketMap.get(bucketKey).tasks += 1;
  });

  return {
    areaValues: bucketKeys.map((k) => bucketMap.get(k).area),
    taskValues: bucketKeys.map((k) => bucketMap.get(k).tasks),
    xLabels: bucketKeys.map((k) => bucketMap.get(k).label),
    xTooltipLabels: bucketKeys.map((k) => bucketMap.get(k).tooltipLabel),
    bucketKeys,
    isWeekendValues: bucketKeys.map((k) => bucketMap.get(k).isWeekend),
    operationalDayCount,
    granularity,
  };
}

export default function PerformanceDashboardPage() {
  const SHOW_VIEW_TOGGLE = false;
  const latestSelectableDate = getLatestSelectableDateValue();

  const [language, setLanguage] = useLanguage();

  const [timePreset, setTimePreset] = useState("month");
  const [multiMonthSpan, setMultiMonthSpan] = useState(3);
  const [fromDate, setFromDate] = useState(() => {
    const { startDate } = resolvePresetRange("month", 3);
    return startDate;
  });
  const [toDate, setToDate] = useState(() => {
    const { endDate } = resolvePresetRange("month", 3);
    return endDate;
  });
  const [building, setBuilding] = useState("all");
  const [floor, setFloor] = useState("all");
  const [room, setRoom] = useState("all");
  const [department, setDepartment] = useState("all");
  const [robot, setRobot] = useState("all");
  const [route, setRoute] = useState("all");
  const [includeDaysNotRunInAverage, setIncludeDaysNotRunInAverage] =
    useState(true);
  const [viewMode, setViewMode] = useState("graph");
  const [performanceRows, setPerformanceRows] = useState([]);
  const [totalRows, setTotalRows] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    buildings: [],
    floors: [],
    rooms: [],
    departments: [],
    robots: [],
    routes: [],
  });

  const t = (key) => {
    const keys = key.split(".");
    let value = performanceTranslations[language];
    for (const currentKey of keys) {
      value = value?.[currentKey];
    }
    return value || key;
  };

  const locale = language === "en" ? "en-US" : "da-DK";

  useEffect(() => {
    if (timePreset === "custom") {
      return;
    }

    const nextRange = resolvePresetRange(timePreset, multiMonthSpan);
    setFromDate(nextRange.startDate);
    setToDate(nextRange.endDate);
  }, [timePreset, multiMonthSpan]);

  const withAllOption = (options) => [
    { value: "all", label: t("filters.all") },
    ...options.map((option) => {
      if (typeof option === "object" && option !== null) {
        return {
          value: String(option.value),
          label: String(option.label ?? option.value),
        };
      }
      return { value: String(option), label: String(option) };
    }),
  ];

  const hasOptionValue = (options, selected) =>
    options.some((option) => String(option) === String(selected));

  const apiDateRange = useMemo(
    () => resolveApiDateRange(fromDate, toDate),
    [fromDate, toDate],
  );

  const apiFilters = useMemo(() => {
    const filters = {
      startDate: apiDateRange.startDate,
      endDate: apiDateRange.endDate,
      includeTotal: true,
      limit: 1000,
      offset: 0,
    };

    if (building !== "all") filters.buildingName = building;
    if (floor !== "all") filters.departmentFloor = floor;
    if (room !== "all") filters.roomName = room;
    if (department !== "all") filters.departmentName = department;
    if (robot !== "all") filters.robotName = robot;
    if (route !== "all") filters.routePkId = route;

    return filters;
  }, [apiDateRange, building, floor, room, department, robot, route]);

  useEffect(() => {
    let isCancelled = false;

    async function loadFilterOptions() {
      try {
        const options = await getCleaningPerformanceFilterOptions({
          startDate: apiDateRange.startDate,
          endDate: apiDateRange.endDate,
          buildingName: building !== "all" ? building : undefined,
          departmentFloor: floor !== "all" ? floor : undefined,
          roomName: room !== "all" ? room : undefined,
          departmentName: department !== "all" ? department : undefined,
        });

        if (!isCancelled) {
          const normalized = {
            buildings: options?.buildings || [],
            floors: options?.floors || [],
            rooms: options?.rooms || [],
            departments: options?.departments || [],
            robots: options?.robots || [],
            routes: options?.routes || [],
          };
          setFilterOptions(normalized);

          if (
            building !== "all" &&
            !hasOptionValue(normalized.buildings, building)
          ) {
            setBuilding("all");
          }
          if (floor !== "all" && !hasOptionValue(normalized.floors, floor)) {
            setFloor("all");
          }
          if (room !== "all" && !hasOptionValue(normalized.rooms, room)) {
            setRoom("all");
          }
          if (
            department !== "all" &&
            !hasOptionValue(normalized.departments, department)
          ) {
            setDepartment("all");
          }
          if (robot !== "all" && !hasOptionValue(normalized.robots, robot)) {
            setRobot("all");
          }
          if (
            route !== "all" &&
            !normalized.routes.some(
              (routeOption) =>
                String(routeOption?.value ?? routeOption) === String(route),
            )
          ) {
            setRoute("all");
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to load performance filter options:", error);
        }
      }
    }

    loadFilterOptions();

    return () => {
      isCancelled = true;
    };
  }, [apiDateRange, building, floor, room, department, robot, route]);

  const clearFilters = () => {
    setBuilding("all");
    setFloor("all");
    setRoom("all");
    setDepartment("all");
    setRobot("all");
    setRoute("all");
    setIncludeDaysNotRunInAverage(true);
  };

  const hasActiveFilters =
    building !== "all" ||
    floor !== "all" ||
    room !== "all" ||
    department !== "all" ||
    robot !== "all" ||
    route !== "all" ||
    includeDaysNotRunInAverage !== true;

  useEffect(() => {
    let isCancelled = false;

    async function loadPerformanceRows() {
      setIsLoading(true);
      setLoadError("");

      try {
        const pageSize = apiFilters.limit || 1000;
        let offset = 0;
        let allRows = [];
        let total = null;

        // Server caps limit per request, so page through offsets until every row is fetched.
        for (;;) {
          const payload = await getCleaningPerformanceRows({
            ...apiFilters,
            limit: pageSize,
            offset,
          });
          const rows = Array.isArray(payload) ? payload : payload?.rows || [];
          const pagination = Array.isArray(payload)
            ? null
            : payload?.pagination;

          allRows = allRows.concat(rows);
          total = pagination?.total ?? total;

          if (isCancelled) {
            return;
          }

          const returned = pagination?.returned ?? rows.length;
          const knownTotal = pagination?.total;
          const hasMore =
            returned > 0 &&
            (typeof knownTotal === "number"
              ? allRows.length < knownTotal
              : returned === pageSize);

          if (!hasMore) {
            break;
          }

          offset += returned;
        }

        if (!isCancelled) {
          setPerformanceRows(allRows);
          setTotalRows(total);
        }
      } catch (error) {
        if (!isCancelled) {
          setLoadError(error?.message || "Failed to fetch dashboard data");
          setPerformanceRows([]);
          setTotalRows(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadPerformanceRows();

    return () => {
      isCancelled = true;
    };
  }, [apiFilters]);

  const filteredRows = useMemo(() => {
    // Show every session that actually cleaned something (area_cleaned > 0),
    // including jobs that completed with an error - they show normally.
    return performanceRows.filter((row) => Number(row?.area_cleaned || 0) > 0);
  }, [performanceRows]);

  const chartBuckets = useMemo(
    () => buildChartBuckets(fromDate, toDate, filteredRows, locale),
    [fromDate, toDate, filteredRows, locale],
  );

  const areaSubtitleKey = useMemo(() => {
    if (chartBuckets.granularity === "day") return "charts.areaSubtitleDay";
    if (chartBuckets.granularity === "week") return "charts.areaSubtitleWeek";
    return "charts.areaSubtitleMonth";
  }, [chartBuckets.granularity]);

  const top10Robots = useMemo(() => {
    const robots = new Map();

    const resolveOperationalDate = (timestamp) => {
      const date = parseBackendTimestamp(timestamp);
      if (!date) return null;

      const operationalDate = new Date(date);
      if (
        date.getHours() < REPORTING_WINDOW_START_HOUR ||
        (date.getHours() === REPORTING_WINDOW_START_HOUR &&
          date.getMinutes() < REPORTING_WINDOW_START_MINUTE)
      ) {
        operationalDate.setDate(operationalDate.getDate() - 1);
      }
      operationalDate.setHours(0, 0, 0, 0);
      return operationalDate;
    };

    filteredRows.forEach((row) => {
      const name = row?.name || "Unknown";
      const operationalDate = resolveOperationalDate(row?.cleaning_start_time);
      const areaCleaned = Number(row?.area_cleaned || 0);
      const possibleArea = Number(
        row?.room_area_minus_inventory_minus_things_on_floor || 0,
      );
      const cappedAreaCleaned =
        Number.isFinite(possibleArea) && possibleArea > 0
          ? Math.min(areaCleaned, possibleArea)
          : areaCleaned;

      if (!robots.has(name)) {
        robots.set(name, {
          name,
          area: 0,
          days: new Map(),
        });
      }

      const current = robots.get(name);
      current.area += cappedAreaCleaned;

      if (!operationalDate) {
        return;
      }

      const day = operationalDate.getDay();
      if (day === 0 || day === 6) {
        return;
      }

      if (!Number.isFinite(possibleArea) || possibleArea <= 0) {
        return;
      }

      const dayKey = toDateInputValue(operationalDate);
      const existingDay = current.days.get(dayKey) || {
        realized: 0,
        possible: 0,
      };
      existingDay.realized += cappedAreaCleaned;
      existingDay.possible += possibleArea;
      current.days.set(dayKey, existingDay);
    });

    const weekdayCountInRange = countWeekdaysInRange(fromDate, toDate);

    return Array.from(robots.values())
      .map((robotItem) => ({
        name: robotItem.name,
        area: Math.round(robotItem.area),
        score: (() => {
          const dailyScores = Array.from(robotItem.days.values()).map((day) =>
            day.possible > 0 ? day.realized / day.possible : 0,
          );
          const activeDayCount = dailyScores.length;
          const denominator = includeDaysNotRunInAverage
            ? weekdayCountInRange
            : activeDayCount;

          if (denominator <= 0) {
            return 0;
          }

          const scoreSum = dailyScores.reduce((sum, value) => sum + value, 0);
          return Math.round((scoreSum / denominator) * 1000) / 10;
        })(),
      }))
      .sort((a, b) => b.score - a.score || b.area - a.area)
      .slice(0, 10);
  }, [filteredRows, fromDate, toDate, includeDaysNotRunInAverage]);

  const areaJobCounts = useMemo(() => {
    let completeJobs = 0;
    let completeWithErrorJobs = 0;

    performanceRows.forEach((row) => {
      if (Number(row?.job_complete_with_error || 0) === 1) {
        completeWithErrorJobs += 1;
      } else if (Number(row?.job_complete || 0) === 1) {
        completeJobs += 1;
      }
    });

    return { completeJobs, completeWithErrorJobs };
  }, [performanceRows]);

  const areaTotals = useMemo(() => {
    const resolveOperationalDate = (timestamp) => {
      const date = parseBackendTimestamp(timestamp);
      if (!date) return null;

      const operationalDate = new Date(date);
      if (
        date.getHours() < REPORTING_WINDOW_START_HOUR ||
        (date.getHours() === REPORTING_WINDOW_START_HOUR &&
          date.getMinutes() < REPORTING_WINDOW_START_MINUTE)
      ) {
        operationalDate.setDate(operationalDate.getDate() - 1);
      }
      operationalDate.setHours(0, 0, 0, 0);
      return operationalDate;
    };

    // filteredRows already requires area_cleaned > 0, so every row here
    // already "qualifies" — kept as its own variable for clarity below.
    const qualifyingRows = filteredRows;

    const activeWeekdayRunDates = new Set();
    filteredRows.forEach((row) => {
      const operationalDate = resolveOperationalDate(row?.cleaning_start_time);
      if (!operationalDate) return;
      const day = operationalDate.getDay();
      if (day === 0 || day === 6) return;
      if (Number(row?.area_cleaned || 0) <= 0) return;
      activeWeekdayRunDates.add(toDateInputValue(operationalDate));
    });

    const bucketPositionMap = new Map(
      (chartBuckets.bucketKeys || []).map((key, index) => [key, index]),
    );
    const baselineByBucketAndRoom = new Map();
    const baselineSourceByBucket = new Array(
      chartBuckets.areaValues.length,
    ).fill(0);
    const possibleByBucket = new Array(chartBuckets.areaValues.length).fill(0);

    filteredRows.forEach((row) => {
      const operationalDate = resolveOperationalDate(row?.cleaning_start_time);
      if (!operationalDate) return;
      const isWeekend =
        operationalDate.getDay() === 0 || operationalDate.getDay() === 6;
      if (isWeekend) return;

      let key;
      if (chartBuckets.granularity === "day") {
        key = toDateInputValue(operationalDate);
      } else if (chartBuckets.granularity === "week") {
        const keys = chartBuckets.bucketKeys || [];
        for (let i = keys.length - 1; i >= 0; i--) {
          if (operationalDate >= new Date(keys[i] + "T00:00:00")) {
            key = keys[i];
            break;
          }
        }
      } else {
        key = `${operationalDate.getFullYear()}-${String(operationalDate.getMonth() + 1).padStart(2, "0")}`;
      }

      const bucketIndex = bucketPositionMap.get(key);
      if (bucketIndex === undefined) return;

      const possibleArea = Number(
        row?.room_area_minus_inventory_minus_things_on_floor || 0,
      );
      if (!Number.isFinite(possibleArea) || possibleArea <= 0) return;

      const roomKey =
        row?.room_pk_id ??
        `${row?.building_name || ""}|${row?.department_name || ""}|${row?.room_name || ""}`;
      const bucketRoomKey = `${bucketIndex}::${roomKey}`;
      const current = baselineByBucketAndRoom.get(bucketRoomKey) || 0;
      if (possibleArea > current) {
        baselineByBucketAndRoom.set(bucketRoomKey, possibleArea);
      }
    });

    baselineByBucketAndRoom.forEach((possibleArea, bucketRoomKey) => {
      const bucketIndex = Number(bucketRoomKey.split("::")[0]);
      if (!Number.isFinite(bucketIndex)) return;
      if (bucketIndex < 0 || bucketIndex >= baselineSourceByBucket.length)
        return;
      baselineSourceByBucket[bucketIndex] += possibleArea;
    });

    qualifyingRows.forEach((row) => {
      const operationalDate = resolveOperationalDate(row?.cleaning_start_time);
      if (!operationalDate) return;
      const isWeekend =
        operationalDate.getDay() === 0 || operationalDate.getDay() === 6;
      if (isWeekend) return;

      let key;
      if (chartBuckets.granularity === "day") {
        key = toDateInputValue(operationalDate);
      } else if (chartBuckets.granularity === "week") {
        const keys = chartBuckets.bucketKeys || [];
        for (let i = keys.length - 1; i >= 0; i--) {
          if (operationalDate >= new Date(keys[i] + "T00:00:00")) {
            key = keys[i];
            break;
          }
        }
      } else {
        key = `${operationalDate.getFullYear()}-${String(operationalDate.getMonth() + 1).padStart(2, "0")}`;
      }

      const bucketIndex = bucketPositionMap.get(key);
      if (bucketIndex === undefined) return;

      const possibleArea = Number(
        row?.room_area_minus_inventory_minus_things_on_floor || 0,
      );
      if (!Number.isFinite(possibleArea) || possibleArea <= 0) return;
      possibleByBucket[bucketIndex] += possibleArea;
    });

    const cappedAreaValues = chartBuckets.areaValues.map((value, index) => {
      const bucketPossible = Number(possibleByBucket[index] || 0);
      if (bucketPossible <= 0) return 0;
      return Math.min(Number(value || 0), bucketPossible);
    });

    const realized = cappedAreaValues.reduce((sum, value) => sum + value, 0);
    const weekdayCountInRange = countWeekdaysInRange(fromDate, toDate);

    const scopeDailyPossibleByRoom = new Map();
    filteredRows.forEach((row) => {
      const roomKey =
        row?.room_pk_id ??
        `${row?.building_name || ""}|${row?.department_name || ""}|${row?.room_name || ""}`;
      const possibleArea = Number(
        row?.room_area_minus_inventory_minus_things_on_floor || 0,
      );
      if (!Number.isFinite(possibleArea) || possibleArea <= 0) return;
      const current = scopeDailyPossibleByRoom.get(roomKey) || 0;
      if (possibleArea > current) {
        scopeDailyPossibleByRoom.set(roomKey, possibleArea);
      }
    });
    const scopeDailyPossible = Array.from(
      scopeDailyPossibleByRoom.values(),
    ).reduce((sum, value) => sum + value, 0);
    const scopeDailyBaseline = scopeDailyPossible * 0.85;

    const possibleFromRuns = possibleByBucket.reduce(
      (sum, value) => sum + value,
      0,
    );
    const possible = includeDaysNotRunInAverage
      ? scopeDailyPossible * weekdayCountInRange
      : possibleFromRuns;
    const missing = Math.max(0, possible - realized);
    const averageBucketCount = includeDaysNotRunInAverage
      ? weekdayCountInRange
      : activeWeekdayRunDates.size;

    const baselineValues = baselineSourceByBucket.map((sum, index) => {
      const isWeekend = chartBuckets.isWeekendValues[index] === true;
      const hasRun = Number(chartBuckets.taskValues[index] || 0) > 0;
      if (isWeekend || !hasRun || sum <= 0) {
        return 0;
      }
      return sum * 0.85;
    });

    const baselineTotal = baselineValues.reduce((sum, value) => sum + value, 0);
    const baselineAverageBucketCount = includeDaysNotRunInAverage
      ? weekdayCountInRange
      : activeWeekdayRunDates.size;
    const baselineAverage = includeDaysNotRunInAverage
      ? baselineAverageBucketCount > 0
        ? (scopeDailyBaseline * baselineAverageBucketCount) /
          baselineAverageBucketCount
        : 0
      : baselineAverageBucketCount > 0
        ? baselineTotal / baselineAverageBucketCount
        : 0;

    return {
      realized,
      possible,
      missing,
      pct: possible > 0 ? (realized / possible) * 100 : 0,
      baseline: Math.round(baselineAverage),
      average:
        averageBucketCount > 0 ? Math.round(realized / averageBucketCount) : 0,
    };
  }, [
    chartBuckets,
    filteredRows,
    includeDaysNotRunInAverage,
    fromDate,
    toDate,
  ]);

  return (
    <>
      <TopNavBar heading="Performance Dashboard" />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          minHeight: "calc(100vh - 64px)",
          py: 3,
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{ px: { xs: 1.5, sm: 2, md: 2.5, lg: 3 } }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #0d3d50 0%, #0a3141 100%)",
              border: "1px solid rgba(136, 208, 230, 0.36)",
              color: "#e7f8fe",
              mb: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={1.2}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#99d2e5", fontWeight: 700 }}
                >
                  {t("topBarLabel")}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {t("title")}
                </Typography>
                <Typography variant="body2" sx={{ color: "#b7deeb" }}>
                  {fromDate} - {toDate}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {isLoading && (
            <Paper
              elevation={0}
              sx={{
                p: 1.4,
                borderRadius: 2,
                border: "1px solid #d6e8ef",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={18} />
              <Typography variant="body2">{t("loading")}</Typography>
            </Paper>
          )}

          {!!loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("errorLoading")}: {loadError}
            </Alert>
          )}

          <PerformanceFiltersCard
            t={t}
            latestSelectableDate={latestSelectableDate}
            timePreset={timePreset}
            setTimePreset={setTimePreset}
            multiMonthSpan={multiMonthSpan}
            setMultiMonthSpan={setMultiMonthSpan}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
            filterOptions={filterOptions}
            withAllOption={withAllOption}
            building={building}
            setBuilding={setBuilding}
            floor={floor}
            setFloor={setFloor}
            department={department}
            setDepartment={setDepartment}
            room={room}
            setRoom={setRoom}
            robot={robot}
            setRobot={setRobot}
            route={route}
            setRoute={setRoute}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            showViewToggle={SHOW_VIEW_TOGGLE}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr 1fr" },
              gridTemplateRows: { xs: "auto", lg: "1fr" },
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                gridColumn: { lg: "1 / 2" },
                gridRow: { lg: "1 / 2" },
                height: "100%",
                minHeight: { xs: 300, sm: 350, md: 400, lg: "auto" },
              }}
            >
              <GraphCard
                title={t("charts.areaTitle")}
                subtitle={t(areaSubtitleKey)}
                unit="m²"
                values={chartBuckets.areaValues}
                isWeekendValues={chartBuckets.isWeekendValues}
                baseline={areaTotals.baseline}
                average={areaTotals.average}
                baselineLabel={t("charts.baseline")}
                averageLabel={t("charts.average")}
                weekendLabel={t("charts.weekendNoRuns")}
                infoTitle={t("charts.calculationInfoTitle")}
                baselineInfoText={t("charts.baselineInfo")}
                averageBaselineInfoText={t("charts.averageBaselineInfo")}
                averageInfoText={t("charts.averageInfo")}
                toggleInfoText={t("charts.toggleInfo")}
                xLabels={chartBuckets.xLabels}
                xTooltipLabels={chartBuckets.xTooltipLabels}
                locale={locale}
                showWeekendLegend={
                  chartBuckets.granularity === "day" &&
                  chartBuckets.isWeekendValues.some((isWeekend) => isWeekend)
                }
              />
            </Box>

            <Box
              sx={{
                gridColumn: { lg: "2 / 3" },
                gridRow: { lg: "1 / 2" },
                height: "100%",
                minHeight: { xs: 300, sm: 350, md: 400, lg: "auto" },
              }}
            >
              <OverviewCard
                title={t("overview.areaTitle")}
                realizedValue={formatNumber(areaTotals.realized, locale)}
                realizedPct={formatPercent(areaTotals.pct, locale)}
                realizedPctNumber={areaTotals.pct}
                planLabel={t("overview.areaPlanLabel")}
                planValue={formatNumber(areaTotals.possible, locale)}
                of={t("overview.of")}
              />
            </Box>

            <Box
              sx={{
                gridColumn: { lg: "3 / 4" },
                gridRow: { lg: "1 / 2" },
                height: "100%",
              }}
            >
              <TopPerformersCard
                title={t("top10.title")}
                chipLabel={t("top10.chip")}
                subtitle={t("top10.subtitle")}
                isLoading={isLoading}
                noDataLabel={t("noData")}
                performers={top10Robots}
                formatNumber={formatNumber}
                locale={locale}
              />
            </Box>
          </Box>

          <Stack spacing={0.25} sx={{ mt: 2, ml: 0.5, maxWidth: 320 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 52px",
                alignItems: "center",
                columnGap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#0f3342", fontWeight: 400, fontSize: "0.86rem" }}
              >
                {t("toggles.includeDaysNotRunInAverage")}
              </Typography>
              <Switch
                size="small"
                checked={includeDaysNotRunInAverage}
                onChange={(event) =>
                  setIncludeDaysNotRunInAverage(event.target.checked)
                }
                color="primary"
                sx={{
                  justifySelf: "end",
                  "& .MuiSwitch-switchBase": { p: 0.35 },
                }}
                inputProps={{
                  "aria-label": t("toggles.includeDaysNotRunInAverage"),
                }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
